// Line items (`type: "line_items"`): pure helpers shared by the designer,
// the operator preview, the task card, the detail/portal/shared views and
// the Excel "Copy table" button.
//
// The contract lives in docs/line-items.md ("Contract (binding for API, web
// and phone)"). It is mirrored, function for function, from
// api/app/utils/line_items.py — read that file's docstrings for the
// reasoning; this file exists so the same summary string, the same totals
// and the same truncation rule come out of the phone, the API and the web
// without three independent guesses. `taskLineItems` additionally tolerates
// a malformed or missing `task.line_items`, because every reader here runs
// on live Firestore data that may have been written by an older app version
// (docs/line-items.md "Older app versions") or hand-edited in the emulator.

const COLUMN_TYPES = ['text', 'number', 'decimal', 'dropdown', 'checkbox', 'scan'];
const NUMERIC_COLUMN_TYPES = ['number', 'decimal'];

export const MAX_COLUMNS = 6;
export const MAX_ROWS = 200;

// Summary string limits (contract): longer than SUMMARY_MAX is cut at the
// last whole row that fits within SUMMARY_CUT, then "; … and N more".
const SUMMARY_MAX = 500;
const SUMMARY_CUT = 480;
const CELL_SEPARATOR = ', ';
const ROW_SEPARATOR = '; ';

const COLUMN_ID_RE = /^[a-z][a-z0-9_]{0,39}$/;

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// --------------------------------------------------------------------------
// Reading the task document
// --------------------------------------------------------------------------

function cleanColumns(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  raw.forEach((col) => {
    if (!isPlainObject(col)) return;
    const cid = col.id;
    if (typeof cid !== 'string' || !cid || seen.has(cid)) return;
    seen.add(cid);
    const clean = { ...col };
    clean.label = (typeof col.label === 'string' && col.label.trim()) ? col.label : cid;
    clean.type = (typeof col.type === 'string' && COLUMN_TYPES.includes(col.type)) ? col.type : 'text';
    clean.total = col.total === true && NUMERIC_COLUMN_TYPES.includes(clean.type);
    out.push(clean);
  });
  return out;
}

function cleanRows(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isPlainObject).map((row) => ({ ...row }));
}

/** Columns from the row keys, for an entry that lost its snapshot. */
function inferColumns(rows) {
  const seen = new Set();
  const out = [];
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (typeof key !== 'string' || key.startsWith('_') || seen.has(key)) return;
      seen.add(key);
      out.push({ id: key, label: key, type: 'text', total: false });
    });
  });
  return out;
}

/**
 * Every line-items field on a task, as `{field_id: {columns, rows}}`.
 *
 * Tolerant of anything: a missing or non-object `line_items`, entries that
 * are not objects, columns without ids, rows that are not objects. Accepts
 * the camelCase `lineItems` spelling as a fallback, as task readers accept
 * both spellings elsewhere (root CLAUDE.md "Firestore field naming"). An
 * entry whose column snapshot is missing gets columns inferred from its row
 * keys, so its values are still shown.
 */
export function taskLineItems(task) {
  if (!isPlainObject(task)) return {};
  let raw = task.line_items;
  if (!isPlainObject(raw)) raw = task.lineItems;
  if (!isPlainObject(raw)) return {};
  const out = {};
  Object.keys(raw).forEach((fieldId) => {
    const entry = raw[fieldId];
    if (typeof fieldId !== 'string' || !isPlainObject(entry)) return;
    const rows = cleanRows(entry.rows);
    let columns = cleanColumns(entry.columns);
    if (columns.length === 0 && rows.length > 0) columns = inferColumns(rows);
    out[fieldId] = { columns, rows };
  });
  return out;
}

/** One field's `{columns, rows}`, or null when the task has none for it. */
export function taskLineItemsField(task, fieldId) {
  const all = taskLineItems(task);
  return Object.prototype.hasOwnProperty.call(all, fieldId) ? all[fieldId] : null;
}

// --------------------------------------------------------------------------
// Values
// --------------------------------------------------------------------------

/** An omitted or blank cell. `0` and `false` are values, not empty. */
export function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return Number.isNaN(value) || !Number.isFinite(value);
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  return false;
}

function formatNumber(value) {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return '';
  // JS numbers never retain a trailing ".0" (5.0 === 5), so this alone
  // satisfies the contract's "numbers without a trailing .0" rule.
  return String(value);
}

/**
 * The text a reader shows for one cell.
 *
 * Checkbox `true`/`false` -> "Yes"/"No"; numbers without a trailing ".0";
 * strings as stored (a dropdown value is already the source option, or the
 * typed "Other" text). An empty cell is "". Tolerates a legacy string
 * "true"/"false" in a checkbox cell, the same way the API's reader does.
 */
export function displayValue(column, value) {
  if (isEmptyValue(value)) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return formatNumber(value);
  if (typeof value === 'string') {
    const ctype = isPlainObject(column) ? column.type : null;
    if (ctype === 'checkbox') {
      const low = value.trim().toLowerCase();
      if (low === 'true' || low === 'yes') return 'Yes';
      if (low === 'false' || low === 'no') return 'No';
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.filter((v) => !isEmptyValue(v)).map((v) => displayValue(column, v)).join(CELL_SEPARATOR);
  }
  return String(value);
}

function numericValue(value) {
  if (typeof value === 'boolean' || value === null || value === undefined) return null;
  if (typeof value === 'number') {
    return (Number.isNaN(value) || !Number.isFinite(value)) ? null : value;
  }
  if (typeof value === 'string') {
    const text = value.trim().replace(/,/g, '');
    if (!text) return null;
    const parsed = parseFloat(text);
    return (Number.isNaN(parsed) || !Number.isFinite(parsed)) ? null : parsed;
  }
  return null;
}

// --------------------------------------------------------------------------
// Summary and totals
// --------------------------------------------------------------------------

function rowText(columns, row) {
  if (!isPlainObject(row)) return '';
  const parts = [];
  (columns || []).forEach((col) => {
    if (!isPlainObject(col) || typeof col.id !== 'string') return;
    const text = displayValue(col, row[col.id]);
    if (text !== '') parts.push(text);
  });
  return parts.join(CELL_SEPARATOR);
}

/**
 * The `formData.<field_id>` summary string, exactly per the contract.
 *
 * Per row: the display values of its non-empty cells in column order,
 * joined with ", ". Rows joined with "; ". Longer than 500 characters is cut
 * at the last whole row that fits within 480 characters, then
 * "; … and N more" is appended (N = rows left out). When not even the first
 * row fits, the result is "… and N more" with no leading rows. No rows: "".
 */
export function summaryText(columns, rows) {
  const cols = Array.isArray(columns) ? columns.filter(isPlainObject) : [];
  const rowList = Array.isArray(rows) ? rows.filter(isPlainObject) : [];
  if (rowList.length === 0) return '';
  const texts = rowList.map((row) => rowText(cols, row));
  const full = texts.join(ROW_SEPARATOR);
  if (full.length <= SUMMARY_MAX) return full;

  let kept = 0;
  let length = 0;
  for (let i = 0; i < texts.length; i += 1) {
    const added = texts[i].length + (i > 0 ? ROW_SEPARATOR.length : 0);
    if (length + added > SUMMARY_CUT) break;
    length += added;
    kept += 1;
  }
  const leftOut = texts.length - kept;
  if (kept === 0) return `… and ${leftOut} more`;
  return `${texts.slice(0, kept).join(ROW_SEPARATOR)}${ROW_SEPARATOR}… and ${leftOut} more`;
}

/**
 * Sums of the `total: true` number/decimal columns, `{col id: number}`.
 *
 * Computed on read, never stored (docs/line-items.md "Design principles" #2:
 * "Totals are computed on read, never stored, so they cannot disagree with
 * the rows"). Only columns marked `total` on a numeric type appear. Missing
 * and non-numeric cells are skipped; a column with no numbers totals 0.
 */
export function columnTotals(columns, rows) {
  const cols = Array.isArray(columns) ? columns : [];
  const rowList = Array.isArray(rows) ? rows.filter(isPlainObject) : [];
  const totals = {};
  cols.forEach((col) => {
    if (!isPlainObject(col)) return;
    const cid = col.id;
    if (typeof cid !== 'string' || col.total !== true) return;
    if (!NUMERIC_COLUMN_TYPES.includes(col.type)) return;
    let total = 0;
    rowList.forEach((row) => {
      const num = numericValue(row?.[cid]);
      if (num !== null) total += num;
    });
    // Round away float drift (0.1 + 0.2) without pretending the sum is more
    // precise than the numbers that went into it.
    totals[cid] = Math.round(total * 1e10) / 1e10;
  });
  return totals;
}

/**
 * `item_label` pluralised for `count` (contract's English rule): the plural
 * is the label plus "s" unless it already ends in "s". An empty label reads
 * as "line". `count === 1` always returns the singular, unpluralised label.
 */
export function pluralLabel(itemLabel, count = 2) {
  const label = (typeof itemLabel === 'string' && itemLabel.trim()) ? itemLabel.trim() : 'line';
  if (count === 1 || label.toLowerCase().endsWith('s')) return label;
  return `${label}s`;
}

/** "3 flavours", "1 flavour", "0 lines" — count + the right form of `item_label`. */
export function countLabel(count, itemLabel) {
  const n = Number(count) || 0;
  return `${n} ${pluralLabel(itemLabel, n)}`;
}

/**
 * The phone-header line: "3 flavours · 12 tubs" — the count plus one
 * segment per `total` column, using that column's own sum and label.
 */
export function headerSummary(columns, rows, itemLabel) {
  const cols = Array.isArray(columns) ? columns : [];
  const rowList = Array.isArray(rows) ? rows : [];
  const totals = columnTotals(cols, rowList);
  const parts = [countLabel(rowList.length, itemLabel)];
  cols.forEach((col) => {
    if (col?.total && Object.prototype.hasOwnProperty.call(totals, col.id)) {
      const label = (col.label || col.id || '').toLowerCase();
      parts.push(`${formatNumber(totals[col.id])} ${label}`.trim());
    }
  });
  return parts.join(' · ');
}

/**
 * "Vanilla 4.5 L ×3" — one row rendered as an inline card/preview line: every
 * column's display value in order, with the last column shown as "×n" when
 * it is a total column (the count the reader checks first), and as
 * "label: value" when it is any other number, a reading such as a temperature.
 */
export function inlineRowText(columns, row) {
  const cols = Array.isArray(columns) ? columns : [];
  if (cols.length === 0) return '';
  const values = cols.map((col) => displayValue(col, row?.[col.id]));
  const lastCol = cols[cols.length - 1];
  const lastValue = values[values.length - 1];
  const rest = values.slice(0, -1).filter((v) => v !== '');
  // "×" reads as a quantity, so only a total column (tubs, cartons) gets it.
  // Any other trailing number is a reading: "Freezer 1: -18", never "×-18".
  if (lastValue !== '' && NUMERIC_COLUMN_TYPES.includes(lastCol?.type)) {
    if (lastCol.total === true) {
      return rest.length > 0 ? `${rest.join(' ')} ×${lastValue}` : `×${lastValue}`;
    }
    return rest.length > 0 ? `${rest.join(' ')}: ${lastValue}` : lastValue;
  }
  return values.filter((v) => v !== '').join(' ');
}

/**
 * Up to `limit` rows rendered with `inlineRowText`, plus how many were left
 * out — the task card's "Vanilla 4.5 L ×3 · Chocolate 4.5 L ×2 · … · + 9 more".
 */
export function cardSummaryLines(columns, rows, limit = 3) {
  const rowList = Array.isArray(rows) ? rows : [];
  const lines = rowList.slice(0, limit).map((row) => inlineRowText(columns, row)).filter(Boolean);
  return { lines, moreCount: Math.max(0, rowList.length - limit) };
}

// --------------------------------------------------------------------------
// Designer: column / fixed-row id generation
// --------------------------------------------------------------------------

/**
 * A `^[a-z][a-z0-9_]{0,39}$` id from a free-typed label, unique against
 * `existingIds`. Used for both column ids and fixed-row ids, which share
 * the contract's id pattern. Generated once, at creation, and never
 * recomputed from a later label edit — see docs/line-items.md's designer
 * spec: "ids generated stable from labels, keep existing ids on edit".
 */
export function generateStableId(label, existingIds) {
  const used = existingIds instanceof Set ? existingIds : new Set(existingIds || []);
  let base = String(label || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!base) base = 'column';
  if (!/^[a-z]/.test(base)) base = `c_${base}`;
  base = base.slice(0, 40);

  let id = base;
  let suffix = 2;
  while (used.has(id)) {
    const tail = `_${suffix}`;
    id = `${base.slice(0, Math.max(1, 40 - tail.length))}${tail}`;
    suffix += 1;
  }
  return id;
}

/**
 * Turns the fixed-rows textarea (one row label per line, so pasting a
 * column from Excel works) into `{id, label}[]`, reusing an existing row's
 * id wherever its label is still present — a label edited elsewhere, or a
 * line simply left alone, keeps the id an operator's in-flight drafts and
 * submitted history already reference. A line whose label is new gets a
 * freshly generated id; a previously-listed label that is no longer in the
 * text is dropped (docs/line-items.md decision #2: "Untouched fixed rows:
 * left out").
 */
export function reconcileFixedRows(text, existingRows) {
  const labels = String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const pool = Array.isArray(existingRows) ? existingRows.slice() : [];
  const usedIds = new Set();
  return labels.map((label) => {
    const matchIndex = pool.findIndex((row) => row && row.label === label);
    if (matchIndex >= 0) {
      const [match] = pool.splice(matchIndex, 1);
      const id = (typeof match.id === 'string' && match.id) ? match.id : generateStableId(label, usedIds);
      usedIds.add(id);
      return { id, label };
    }
    const id = generateStableId(label, usedIds);
    usedIds.add(id);
    return { id, label };
  });
}

// --------------------------------------------------------------------------
// Validation (designer + wizard step gate)
// --------------------------------------------------------------------------

function columnDisplayName(col, index) {
  return col?.label || col?.id || `Column ${index + 1}`;
}

/**
 * Validates a field's `line_items` config against the contract's rules, for
 * inline designer feedback. Mirrors the API's write-path validation (the
 * fast feedback, not the guarantee — see utils/issueFieldBindings.js for the
 * same pattern with `maps_to`). Returns an array of human-readable messages;
 * empty means valid.
 */
export function validateLineItemsConfig(config) {
  const errors = [];
  const cfg = isPlainObject(config) ? config : {};

  const rowsMode = cfg.rows || 'free';
  if (!['free', 'fixed'].includes(rowsMode)) {
    errors.push('Rows must be "Operator adds lines" or "Fixed list".');
  }

  const minRows = Number.isInteger(cfg.min_rows) ? cfg.min_rows : 0;
  const maxRows = Number.isInteger(cfg.max_rows) ? cfg.max_rows : MAX_ROWS;
  if (minRows < 0) errors.push('Minimum lines cannot be negative.');
  if (maxRows < 1 || maxRows > MAX_ROWS) errors.push(`Maximum lines must be between 1 and ${MAX_ROWS}.`);
  if (minRows > maxRows) errors.push('Minimum lines cannot be more than maximum lines.');

  const columns = Array.isArray(cfg.columns) ? cfg.columns : [];
  if (columns.length === 0) errors.push('Add at least one column.');
  if (columns.length > MAX_COLUMNS) errors.push(`At most ${MAX_COLUMNS} columns are allowed.`);

  const seenIds = new Set();
  columns.forEach((col, index) => {
    if (!isPlainObject(col)) {
      errors.push(`Column ${index + 1} is invalid.`);
      return;
    }
    const name = columnDisplayName(col, index);
    if (!col.label || !String(col.label).trim()) {
      errors.push(`Column ${index + 1}: needs a label.`);
    }
    const id = col.id;
    if (!id || !COLUMN_ID_RE.test(id)) {
      errors.push(`${name}: the column id must be lowercase letters, numbers and underscores, starting with a letter.`);
    } else if (seenIds.has(id)) {
      errors.push(`Two columns share the id "${id}".`);
    } else {
      seenIds.add(id);
    }
    if (!COLUMN_TYPES.includes(col.type)) {
      errors.push(`${name}: unknown column type.`);
    }
    if (col.type === 'dropdown') {
      const options = Array.isArray(col.options) ? col.options.filter((o) => String(o || '').trim()) : [];
      if (options.length === 0) errors.push(`${name}: a dropdown column needs at least one option.`);
    }
    if (col.allow_other && col.type !== 'dropdown') {
      errors.push(`${name}: "Allow other" only applies to a dropdown column.`);
    }
    const hasRange = (col.min_value !== undefined && col.min_value !== null)
      || (col.max_value !== undefined && col.max_value !== null);
    if ((hasRange || col.total || col.stepper) && !NUMERIC_COLUMN_TYPES.includes(col.type)) {
      errors.push(`${name}: min/max, Total and the stepper only apply to a number or decimal column.`);
    }
    if (col.stepper && col.type !== 'number') {
      errors.push(`${name}: the stepper only applies to a number column.`);
    }
    if (col.repeat_last && col.type === 'checkbox') {
      errors.push(`${name}: "Repeat last value" cannot be used with a checkbox column.`);
    }
  });

  if (rowsMode === 'fixed') {
    const fixedRows = Array.isArray(cfg.fixed_rows) ? cfg.fixed_rows : [];
    if (fixedRows.length === 0) errors.push('A fixed list needs at least one row.');
    if (fixedRows.length > MAX_ROWS) errors.push(`At most ${MAX_ROWS} fixed rows are allowed.`);
    const seenRowIds = new Set();
    fixedRows.forEach((row, index) => {
      if (!isPlainObject(row) || !row.label || !String(row.label).trim()) {
        errors.push(`Fixed row ${index + 1}: needs a label.`);
      }
      const rid = row?.id;
      if (!rid || !COLUMN_ID_RE.test(rid)) {
        errors.push(`Fixed row ${index + 1}: invalid row id.`);
      } else if (seenRowIds.has(rid)) {
        errors.push(`Two fixed rows share the id "${rid}".`);
      } else {
        seenRowIds.add(rid);
      }
    });
    if (columns[0] && columns[0].type !== 'text') {
      errors.push('The first column holds the row label in a fixed list, so its type must be Text.');
    }
    if (columns.length < 2) {
      errors.push('A fixed list needs at least one column besides the row label.');
    }
  }

  return errors;
}

// --------------------------------------------------------------------------
// Operator preview (web "Operator sees" panel)
// --------------------------------------------------------------------------

const SAMPLE_TEXT = ['Sample', 'Example'];
const SAMPLE_SCAN = ['ABCD1234567', 'WXYZ7654321'];

/** One made-up value for a column, so the preview can draw a row that looks
 * like real data rather than an empty grid — docs/line-items.md "Operator
 * sees preview": "two sample rows made from the column types". */
function sampleCellValue(column, index) {
  switch (column?.type) {
    case 'number': return index + 1;
    case 'decimal': return index + 1.5;
    case 'dropdown': {
      const options = Array.isArray(column.options) ? column.options.filter(Boolean) : [];
      return options.length > 0 ? options[index % options.length] : 'Option';
    }
    case 'checkbox': return index % 2 === 0;
    case 'scan': return SAMPLE_SCAN[index % SAMPLE_SCAN.length];
    case 'text':
    default:
      return SAMPLE_TEXT[index % SAMPLE_TEXT.length];
  }
}

/** `count` made-up rows across `columns`, keyed by column id like a real row. */
export function sampleLineItemsRows(columns, count = 2) {
  const cols = Array.isArray(columns) ? columns : [];
  return Array.from({ length: count }, (_, index) => {
    const row = {};
    cols.forEach((col) => {
      if (col?.id) row[col.id] = sampleCellValue(col, index);
    });
    return row;
  });
}

// --------------------------------------------------------------------------
// Older app versions (docs/line-items.md "Older app versions")
// --------------------------------------------------------------------------

/** The first phone build that renders Line items (docs/line-items.md "Minimum phone build"). */
export const MIN_LINE_ITEMS_VERSION_CODE = 67;

/**
 * The designer's app-version warning text, or null when there is nothing to
 * warn about (no active operators, or every active operator is known to be
 * on a build that renders Line items).
 *
 * Operators with no recorded `app_version_code` are reported separately as
 * "unknown" rather than folded into either bucket: the field is only
 * written at login (mobile `ActLogin.trackOperatorLogin`), so an operator
 * who has not opened the app since it started being recorded has nothing
 * stored yet and may or may not be on an old build.
 */
export function appVersionWarningText(
  { activeCount = 0, outdatedCount = 0, unknownCount = 0 } = {},
  minVersionCode = MIN_LINE_ITEMS_VERSION_CODE
) {
  if (activeCount === 0 || (outdatedCount === 0 && unknownCount === 0)) return null;
  const parts = [];
  if (outdatedCount > 0) {
    parts.push(
      activeCount === 1
        ? `${outdatedCount} of ${activeCount} active operator uses an app version that shows this as a text box.`
        : `${outdatedCount} of ${activeCount} active operators use an app version that shows this as a text box.`
    );
  }
  if (unknownCount > 0) {
    parts.push(
      outdatedCount > 0
        ? `${unknownCount} more ${unknownCount === 1 ? 'has' : 'have'} not signed in since this was added, `
          + 'so their app version is not known.'
        : `${unknownCount} of ${activeCount} active ${activeCount === 1 ? 'operator has' : 'operators have'} `
          + 'not signed in since this was added, so their app version is not known.'
    );
  }
  parts.push('They need the latest TrakLog app.');
  return parts.join(' ');
}

// --------------------------------------------------------------------------
// Export
// --------------------------------------------------------------------------

/**
 * Tab-separated text of the table, for "Copy table": a header row (`#` plus
 * each column's label), one row per line item, and a bold-in-spirit Total
 * row when any column totals. Pasting into Excel or an email gives a real
 * table, per docs/line-items.md "Task detail view, customer portal, shared page".
 */
export function toTsv(columns, rows) {
  const cols = Array.isArray(columns) ? columns : [];
  const rowList = Array.isArray(rows) ? rows : [];
  const header = ['#', ...cols.map((c) => c.label || c.id)].join('\t');
  const lines = rowList.map((row, index) => (
    [String(index + 1), ...cols.map((c) => displayValue(c, row?.[c.id]))].join('\t')
  ));
  const totals = columnTotals(cols, rowList);
  if (Object.keys(totals).length > 0) {
    const totalRow = cols.map((col, index) => {
      if (index === 0) return 'Total';
      return Object.prototype.hasOwnProperty.call(totals, col.id) ? formatNumber(totals[col.id]) : '';
    });
    lines.push(['', ...totalRow].join('\t'));
  }
  return [header, ...lines].join('\n');
}
